/* eslint-disable max-len */
/* eslint-disable react/require-default-props */
/* eslint-disable react/prop-types */
import { TableColumn } from 'react-data-table-component';
import { DefaultTable } from 'src/components/default-table';
import { RegularButton } from 'pg-ui-library';
import { BenefitSettingsType, IBenefit, IDependent } from 'src/interfaces';
import React, { useState } from 'react';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { useTranslation } from 'react-i18next';

import { BeneficiariesListContainer, BeneficiariesListFooter, BeneficiariesListRightBottom } from './styles';

const columns: TableColumn<IDependent>[] = [
  {
    name: 'Name',
    selector: (row: IDependent) => row.firstName as unknown as '',
  },
  {
    name: 'Relationship',
    selector: (row: IDependent) => row.relationship as unknown as '',
  },
  {
    name: 'SSN',
    selector: (row: IDependent) => row.ssn as unknown as '',
  },
  {
    name: 'Zip Code',
    selector: (row: IDependent) => row.zipCode as unknown as '',
  },
  {
    name: 'Birth Date',
    selector: (row: IDependent) => row.birthDate as unknown as '',
  },
  {
    name: 'Email',
    selector: (row: IDependent) => row.email as unknown as '',
  },
];

const customStyles = {
  headRow: {
    style: {
      border: 'none',
    },
  },
  headCells: {
    style: {
      color: '#9399B2',
      font: 'normal normal bold 16px Biotif',
    },
  },
  rows: {
    style: {
      font: 'normal normal normal 14px Biotif',
      color: '#1B1D35',
      borderRadius: '5px',
      '&:not(:last-of-type)': {
        borderBottomWidth: '0px',
      },
    },
    stripedStyle: {
      color: '#1B1D35',
      backgroundColor: '#F3F4F8',
    },
    highlightOnHoverStyle: {
      backgroundColor: 'rgb(230, 244, 244)',
      borderBottomColor: '#FFFFFF',
      borderRadius: '25px',
      outline: '1px solid #FFFFFF',
    },
  },
  pagination: {
    style: {
      border: 'none',
      svg: {
        fill: 'rgba(0,0,0,0.54)',
      },
    },
  },
};

type Initialized = {
  currentBenefitId: string;
  isInitialized: boolean;
};

const initalized: Initialized = { currentBenefitId: '', isInitialized: false };

export const DependentsSettingsForm: React.FC<{
  benefit: IBenefit;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (benefit: IBenefit) => void;
  onRequestNewBeneficiarie?: () => void;
  onRequestBack?: () => void;
}> = ({ benefit, onSubmit, onRequestNewBeneficiarie, onRequestBack }) => {
  const { t } = useTranslation();
  const { data } = useTypedSelector(state => state.personalInfo);

  const [selectedDependents, setSelectedDependents] = useState<IDependent[]>([]);

  const list = (data?.personalInfo?.dependents ?? []).concat(data?.personalInfo?.emergencyContacts ?? []);

  const handleSelection = ({ selectedRows }: { selectedRows: IDependent[] }) => {
    setSelectedDependents(selectedRows);
  };

  const filterData = () => {
    if (benefit.dependents === 'EE+SP') {
      return list.filter(
        item =>
          item.isDependent &&
          (item.relationship === 'Husband' || item.relationship === 'Wife' || item.relationship === 'Spouse')
      );
    }
    if (benefit.dependents === 'EE+C') {
      return list.filter(
        item =>
          item.isDependent &&
          (item.relationship === 'Son' || item.relationship === 'Child' || item.relationship === 'Daughter')
      );
    }
    if (benefit.dependents === 'FAM') {
      return list.filter(
        item =>
          item.isDependent ||
          item.relationship === 'N/A' ||
          item.relationship === 'Son' ||
          item.relationship === 'Husband' ||
          item.relationship === 'Daughter' ||
          item.relationship === 'Wife'
      );
    }
    if (benefit.dependents === 'EE') {
      return list.filter(
        item =>
          item.isDependent &&
          (item.relationship === 'Son' ||
            item.relationship === 'Child' ||
            item.relationship === 'Daughter' ||
            item.relationship === 'Spouse')
      );
    }
    return list;
  };

  const removeDuplicateDependentsById = (arr: IDependent[]): IDependent[] => {
    const seen: { [key: string]: boolean } = {};
    return arr.filter(obj => {
      // Some endpoints return the same person in both dependents and emergencyContacts.
      // Use a stable identity key to avoid duplicates in the FAM selection table.
      const identityKey =
        (obj.personId as string) ||
        (obj.id as string) ||
        `${obj.firstName || ''}-${obj.relationship || ''}-${obj.ssn || ''}-${obj.birthDate || ''}`;

      if (seen[identityKey]) {
        return false;
      }
      seen[identityKey] = true;
      return true;
    });
  };

  const filteredData = removeDuplicateDependentsById(filterData());

  const submit = () => {
    const values = selectedDependents.map(b => ({
      personId: b.personId as string,

      name: b.firstName,
    }));

    onSubmit({
      ...benefit,
      settings: {
        rates: benefit.settings?.rates,
        type: benefit.settings?.type ? benefit.settings?.type : BenefitSettingsType.LIFE_INSURANCE,
        data: { ...benefit.settings?.data, dependents: values },
      },
    });
  };

  const rowSelectCritera = (item: IDependent) => {
    initalized.currentBenefitId = benefit.benefitId;
    initalized.isInitialized = true;
    if (benefit.dependents === 'EE+SP') {
      return item.relationship === 'Husband' || item.relationship === 'Wife' || item.relationship === 'Spouse';
    }
    if (benefit.dependents === 'EE+C') {
      return item.relationship === 'Son' || item.relationship === 'Child' || item.relationship === 'Daughter';
    }
    if (benefit.dependents === 'FAM') {
      return (
        item.isDependent ||
        item.relationship === 'N/A' ||
        item.relationship === 'Son' ||
        item.relationship === 'Husband' ||
        item.relationship === 'Daughter' ||
        item.relationship === 'Wife'
      );
    }

    return false;
  };

  return (
    <>
      <BeneficiariesListContainer>
        <h4>Select your Dependent(s)</h4>
        {initalized.isInitialized && initalized.currentBenefitId === benefit.benefitId ? (
          <DefaultTable
            columns={columns}
            data={filteredData || []}
            customStyles={customStyles}
            selectableRows
            onSelectedRowsChange={handleSelection}
          />
        ) : (
          <DefaultTable
            columns={columns}
            data={filteredData || []}
            customStyles={customStyles}
            selectableRows
            onSelectedRowsChange={handleSelection}
            selectableRowSelected={rowSelectCritera}
          />
        )}
      </BeneficiariesListContainer>
      <BeneficiariesListFooter>
        {onRequestBack && <RegularButton onClick={onRequestBack}>{t('global.button.back')}</RegularButton>}
        <BeneficiariesListRightBottom>
          <div>
            {onRequestNewBeneficiarie && (
              <RegularButton onClick={onRequestNewBeneficiarie}>{t('benefits.beneficiaries.title')}</RegularButton>
            )}
            <RegularButton disabled={!selectedDependents?.length} onClick={submit}>
              {onRequestBack ? t('global.form.continue') : t('global.form.submit')}
            </RegularButton>
          </div>
        </BeneficiariesListRightBottom>
      </BeneficiariesListFooter>
    </>
  );
};
